import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages, Check, ChevronDown } from "lucide-react";
import { StarOfDavid } from "./StarOfDavid";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { scrollToSelector } from "@/hooks/useLenis";
import { EditableText } from "@/components/site/edit/EditableText";

const links = [
  { key: "nav.home", href: "#home" },
  { key: "nav.programs", href: "#programs" },
  { key: "nav.events", to: "/events" },
  { key: "nav.holidays", to: "/holidays" },
  { key: "nav.services", to: "/services" },
  { key: "nav.about", to: "/about" },
  { key: "nav.gallery", href: "#gallery" },
  { key: "nav.contact", href: "#contact" },
];

const LANGS = [
  { code: "en", label: "English" },
  { code: "cs", label: "Čeština" },
  { code: "he", label: "עברית" },
  { code: "ru", label: "Русский" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLang, enabledLangs } = useLang();
  const visibleLangs = LANGS.filter((l) => enabledLangs.includes(l.code));
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const siteName = settings?.site_name?.trim() || "Chabad of Prague";
  const nav = settings?.nav_visibility || {};
  const visLinks = links.filter((l) => nav[l.key.split(".")[1]] !== false);
  const showDonate = nav.donate !== false;

  useEffect(() => { if (siteName) document.title = siteName; }, [siteName]);

  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.to) {
      navigate(link.to);
      window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/${link.href}`);
      return;
    }
    scrollToSelector(link.href);
  };

  const goDonate = () => {
    setOpen(false);
    if (location.pathname !== "/") { navigate("/#donate"); return; }
    scrollToSelector("#donate");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,box-shadow] duration-300 ${
        scrolled
          ? "bg-[#F9F8F6]/95 md:bg-[#F9F8F6]/80 backdrop-blur-none md:backdrop-blur-xl border-b border-[#0F172A]/5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go({ href: "#home" })}
          className="flex items-center gap-2 group"
        >
          <span className="w-10 h-10 flex items-center justify-center">
            <img src="/logo.png" alt={`${siteName} logo`} className="w-full h-full object-contain" />
          </span>
          <span className="font-serif text-xl tracking-tight">{siteName}</span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {visLinks.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-${l.key.split(".")[1]}`}
              onClick={() => go(l)}
              className="text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#BE923C] hover:after:w-full after:transition-[width] after:duration-300"
            >
              {t(l.key)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="nav-lang-toggle"
              onClick={() => setLangOpen((o) => !o)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              aria-label="Switch language"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/15 px-3.5 py-2 text-sm font-semibold text-[#0F172A] hover:border-[#BE923C] hover:text-[#BE923C] transition-colors"
            >
              <Languages size={16} /> {currentLang.label} <ChevronDown size={13} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  data-testid="lang-menu"
                  className="absolute end-0 mt-2 w-40 rounded-2xl bg-white border border-[#0F172A]/10 shadow-[0_12px_40px_rgba(15,23,42,0.12)] overflow-hidden z-50"
                >
                  {visibleLangs.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onMouseDown={(e) => { e.preventDefault(); setLang(l.code); setLangOpen(false); }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
                        lang === l.code ? "bg-[#FBF7EE] text-[#BE923C]" : "text-[#0F172A] hover:bg-[#F1EEE8]"
                      }`}
                    >
                      {l.label} {lang === l.code && <Check size={15} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            data-testid="nav-donate-btn"
            onClick={goDonate}
            className={`${showDonate ? "hidden sm:inline-flex" : "hidden"} items-center rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white text-sm font-semibold px-6 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#BE923C] focus:ring-offset-2 focus:ring-offset-[#F9F8F6]`}
          >
            <EditableText k="nav.donate" as="span" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 text-[#0F172A]"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#F9F8F6]/95 backdrop-blur-xl border-b border-[#0F172A]/5"
          >
            <div className="px-5 py-5 flex flex-col gap-1">
              {visLinks.map((l) => (
                <button
                  key={l.key}
                  onClick={() => go(l)}
                  className="text-left py-3 text-base font-medium text-[#0F172A]"
                >
                  {t(l.key)}
                </button>
              ))}
              {showDonate && (
                <button
                  onClick={goDonate}
                  className="mt-2 rounded-full bg-[#BE923C] text-white font-semibold py-3"
                >
                  <EditableText k="nav.donate" as="span" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
